
/**
 * Geocodificador L4 usando la API OGC Features del IGN/IDEE.
 *
 * Consulta la colección de unidades administrativas del Instituto
 * Geográfico Nacional (las de direcciones y nombres geográficos ya no existen).
 *
 * @module services/geocoding/generic
 * @version 1.0.0
 */

import type { AxiosInstance, AxiosError } from 'axios';
import axios from 'axios';
import proj4 from 'proj4';
import type { GeocodedCoordinates } from './CartoCiudadGeocoder';
import type { GeocodingResult as TypedResult } from '../../../types/errors';
import {
  createErrorContext,
  createError,
  success,
  failure,
} from '../../../types/errors';

// Asegurar proyecciones
if (!proj4.defs('EPSG:25830')) {
  proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
}
if (!proj4.defs('EPSG:4326')) {
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
}

/** Feature GeoJSON de la API OGC del IGN */
interface IDEEFeature {
  type: 'Feature';
  geometry: {
    type: string;
    coordinates: number[] | number[][] | number[][][];
  };
  properties: {
    name?: string;
    text?: string;
    municipio?: string;
    provincia?: string;
    comunidadAutonoma?: string;
    codIne?: string;
    inspireid_localid?: string;
    [key: string]: string | number | boolean | undefined;
  };
}

/** Respuesta GeoJSON FeatureCollection */
interface IDEEFeatureCollection {
  type: 'FeatureCollection';
  features: IDEEFeature[];
  numberMatched?: number;
  numberReturned?: number;
}

/** Opciones de búsqueda IDEE */
export interface IDEESearchOptions {
  /** Texto de búsqueda (nombre de lugar, dirección) */
  query: string;
  /** Municipio para filtrado */
  municipality?: string;
  /** Provincia para filtrado */
  province?: string;
  /** Máximo de resultados */
  maxResults?: number;
}

/**
 * Colecciones disponibles en la API OGC del IGN
 * Cada una tiene datos distintos y complementarios
 */
// CDAUWFS (24-sep-2026): `address` y `geographicalname` ya no existen en
// api-features.ign.es (404 «Collection not found»); `/collections` solo lista, de
// las tres, `administrativeunit`. Ver .ptel/p0_cdauwfs_20260923/AUDIT_CDAUWFS.md.
const IDEE_COLLECTIONS = [
  'administrativeunit', // Unidades administrativas
] as const;

/**
 * Geocodificador L4 usando la API OGC Features del IGN/IDEE.
 *
 * Consulta la colección de unidades administrativas del Instituto
 * Geográfico Nacional (las de direcciones y nombres geográficos ya no existen).
 *
 * @example
 * ```typescript
 * const geocoder = new IDEEGeocoder();
 * const result = await geocoder.geocode({
 *   query: 'Ayuntamiento',
 *   municipality: 'Colomera',
 *   province: 'Granada'
 * });
 * ```
 */
export class IDEEGeocoder {
  private static readonly BASE_URL = 'https://api-features.ign.es';
  private axiosInstance: AxiosInstance;
  private cache: Map<string, GeocodedCoordinates>;

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 12000,
      headers: {
        'Accept': 'application/json',
      },
    });
    this.cache = new Map();
  }

  /**
   * Geocodifica un nombre de lugar o dirección mediante IDEE
   */
  public async geocode(options: IDEESearchOptions): Promise<TypedResult<GeocodedCoordinates>> {
    const cacheKey = this.buildCacheKey(options);
    const startTime = Date.now();

    const errorContext = createErrorContext('idee', 'L4', {
      elementName: options.query,
      municipio: options.municipality,
      provincia: options.province,
    });

    // Caché
    if (this.cache.has(cacheKey)) {
      return success(this.cache.get(cacheKey)!, {
        processingTime: Date.now() - startTime,
        sourcesConsulted: ['idee'],
        finalSource: 'cache',
        cascadeLevel: 'L0',
        fromCache: true,
      });
    }

    try {
      // Intentar cada colección en orden de prioridad
      for (const collection of IDEE_COLLECTIONS) {
        const result = await this.searchCollection(collection, options);
        if (result) {
          this.cache.set(cacheKey, result);
          return success(result, {
            processingTime: Date.now() - startTime,
            sourcesConsulted: ['idee'],
            finalSource: 'idee',
            cascadeLevel: 'L4',
            fromCache: false,
          });
        }
      }

      return failure(
        createError.noResults(
          errorContext,
          options.query,
          { municipality: options.municipality || '', province: options.province || '' },
          true,
        ),
      );
    } catch (error) {
      return this.handleError(error as AxiosError, errorContext);
    }
  }

  /**
   * Geocodifica infraestructura (wrapper para orquestador)
   */
  public async geocodeInfrastructure(
    name: string,
    municipality: string,
    province: string,
  ): Promise<TypedResult<GeocodedCoordinates>> {
    return this.geocode({ query: name, municipality, province });
  }

  /**
   * Busca en una colección específica de la API OGC
   */
  private async searchCollection(
    collection: string,
    options: IDEESearchOptions,
  ): Promise<GeocodedCoordinates | null> {
    try {
      const query = this.buildBboxQuery(options);
      const limit = options.maxResults || 5;

      // Construir URL con parámetros de búsqueda textual
      const params = new URLSearchParams({
        limit: String(limit),
        f: 'json',
      });

      // Filtro textual (nombre + municipio)
      params.append('q', query);

      const url = `${IDEEGeocoder.BASE_URL}/collections/${collection}/items?${params.toString()}`;
      const response = await this.axiosInstance.get<IDEEFeatureCollection>(url);

      if (!response.data?.features?.length) {
        return null;
      }

      // Filtrar por municipio/provincia si se especificaron
      const filtered = this.filterByLocation(response.data.features, options);
      if (filtered.length === 0) return null;

      // Tomar el mejor resultado
      const best = filtered[0];
      return this.featureToCoordinates(best, collection);
    } catch {
      // Silenciar errores de colecciones individuales
      return null;
    }
  }

  /**
   * Filtra features por coincidencia con municipio/provincia
   */
  private filterByLocation(
    features: IDEEFeature[],
    options: IDEESearchOptions,
  ): IDEEFeature[] {
    if (!options.municipality && !options.province) return features;

    const muniNorm = options.municipality
      ? this.normalize(options.municipality)
      : null;
    const provNorm = options.province
      ? this.normalize(options.province)
      : null;

    return features.filter((f) => {
      const props = f.properties;
      const fMuni = props.municipio ? this.normalize(props.municipio) : '';
      const fProv = props.provincia ? this.normalize(props.provincia) : '';

      const muniOk = !muniNorm || fMuni.includes(muniNorm) || muniNorm.includes(fMuni);
      const provOk = !provNorm || fProv.includes(provNorm) || provNorm.includes(fProv);

      return muniOk && provOk;
    });
  }

  /**
   * Convierte un Feature GeoJSON en coordenadas geocodificadas
   */
  private featureToCoordinates(
    feature: IDEEFeature,
    collection: string,
  ): GeocodedCoordinates | null {
    const coords = this.extractPointCoords(feature.geometry);
    if (!coords) return null;

    const [lng, lat] = coords;
    if (isNaN(lng) || isNaN(lat)) return null;

    // WGS84 → UTM30 ETRS89
    const [x, y] = proj4('EPSG:4326', 'EPSG:25830', [lng, lat]);

    // Confianza según colección
    const confidence = 45; // administrativeunit (única colección que queda)

    const props = feature.properties;

    return {
      x,
      y,
      confidence,
      source: 'IDEE',
      sourceLayer: collection,
      matchedName: props.name || props.text || 'Unknown',
      matchType: collection,
      municipality: props.municipio,
      province: props.provincia,
      muniCode: props.codIne,
      metadata: {
        collection,
        comunidadAutonoma: props.comunidadAutonoma,
        inspireId: props.inspireid_localid,
      },
    };
  }

  /**
   * Extrae coordenadas [lng, lat] de una geometría GeoJSON
   */
  private extractPointCoords(geometry: IDEEFeature['geometry']): [number, number] | null {
    if (!geometry?.coordinates) return null;

    if (geometry.type === 'Point') {
      const c = geometry.coordinates as number[];
      return [c[0], c[1]];
    }

    // Para polígonos/líneas, usar centroide aproximado
    if (geometry.type === 'Polygon') {
      const ring = (geometry.coordinates as number[][][])[0];
      if (!ring?.length) return null;
      const sumLng = ring.reduce((s, p) => s + p[0], 0);
      const sumLat = ring.reduce((s, p) => s + p[1], 0);
      return [sumLng / ring.length, sumLat / ring.length];
    }

    if (geometry.type === 'MultiPoint') {
      const points = geometry.coordinates as number[][];
      if (!points?.length) return null;
      return [points[0][0], points[0][1]];
    }

    return null;
  }

  /**
   * Construye query de búsqueda con contexto geográfico
   */
  private buildBboxQuery(options: IDEESearchOptions): string {
    const parts = [options.query];
    if (options.municipality && !options.query.toLowerCase().includes(options.municipality.toLowerCase())) {
      parts.push(options.municipality);
    }
    return parts.join(' ');
  }

  /** Normaliza texto para comparación */
  private normalize(text: string): string {
    return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  }

  /** Construye clave de caché */
  private buildCacheKey(options: IDEESearchOptions): string {
    return `idee|${options.query}|${options.municipality || ''}|${options.province || ''}`.toLowerCase();
  }

  /** Gestiona errores de red */
  private handleError(error: AxiosError, errorContext: ReturnType<typeof createErrorContext>): TypedResult<GeocodedCoordinates> {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return failure(createError.timeout(errorContext, IDEEGeocoder.BASE_URL, 12000));
    }
    if (error.response?.status === 429) {
      return failure(createError.rateLimit(errorContext, IDEEGeocoder.BASE_URL, 60));
    }
    if (error.response?.status && error.response.status >= 500) {
      return failure(createError.serviceUnavailable(errorContext, IDEEGeocoder.BASE_URL, error.response.status));
    }
    return failure(createError.connection(errorContext, IDEEGeocoder.BASE_URL, 'unknown'));
  }

  /** Limpia caché */
  public clearCache(): void { this.cache.clear(); }

  /** Estadísticas */
  public getStats(): { cacheSize: number; endpoint: string } {
    return { cacheSize: this.cache.size, endpoint: IDEEGeocoder.BASE_URL };
  }

  /** Reset estadísticas (compatibilidad orquestador) */
  public resetStats(): void { this.cache.clear(); }
}

export default IDEEGeocoder;
